import Immutable, { List } from 'immutable';
import { ProjectGroupEdit } from 'records/ProjectGroupEdit';
import { AppState, AppStateActionParams } from 'records/AppState';
import { User, UserMap } from 'records/User';
import {
  addUser,
  canPurchase,
  getUsers,
  removeUserFromProject,
} from 'services/userService';
import { ProjectGroupUserAddition } from 'records/ProjectGroupUserAddition';
import { BatchProjectGroupsEdit } from 'records/BatchProjectGroupsEdit';
import { batchEditProjectGroups } from 'services/groupService';

export interface UserActions {
  getUsers: (actionParams: AppStateActionParams) => Promise<AppState>;

  addUser: (
    actionParams: AppStateActionParams,
    email: string,
    role: string,
    groupIds?: string[]
  ) => Promise<AppState>;

  removeUsersFromProject: (
    actionParams: AppStateActionParams,
    usersToRemove: List<User>
  ) => Promise<AppState>;

  setSelectedUsers: (
    actionParams: AppStateActionParams,
    user: User
  ) => AppState;

  clearAllSelectedUsers: (actionParams: AppStateActionParams) => AppState;

  canPurchase: (actionParams: AppStateActionParams) => Promise<AppState>;
}

const userActions = {
  getUsers: async ({ state }: AppStateActionParams): Promise<AppState> => {
    const { selectedProject } = state();

    const users = await getUsers(selectedProject);

    const usersByKey = users.reduce(
      (acc: UserMap, u: User): UserMap => acc.set(u.id, u),
      Immutable.OrderedMap()
    );

    return state().set('users', usersByKey);
  },

  addUser: async (
    { state, actions }: AppStateActionParams,
    email: string,
    role: string,
    groupIds?: string[]
  ): Promise<AppState> => {
    const { selectedProject } = state();

    const addedUser = await addUser(selectedProject, email, role);

    await actions.getRoles();

    const addToGroup = groupIds && groupIds.length > 0;
    let updatedGroups;
    if (addToGroup) {
      const userGroupAddition = new ProjectGroupUserAddition({
        email: addedUser.email,
        id: addedUser.id,
      });
      const groupEdits = groupIds?.map((id) => {
        const groupEdit = new ProjectGroupEdit({
          groupid: id,
          usersToRemove: [],
          usersToAdd: [userGroupAddition],
        });

        return groupEdit;
      });
      updatedGroups = new BatchProjectGroupsEdit({
        projectid: selectedProject,
        groupEdits,
      });

      updatedGroups = await batchEditProjectGroups(updatedGroups);
    }

    const updatedState = addToGroup
      ? state()
          .update(
            'users',
            (users): UserMap => users.set(addedUser.id, addedUser)
          )
          .updateIn(['projects', selectedProject, 'assignees'], (assignees) =>
            assignees.add(addedUser.email)
          )
          .setIn(['projectGroups'], updatedGroups)
      : state()
          .update(
            'users',
            (users): UserMap => users.set(addedUser.id, addedUser)
          )
          .updateIn(['projects', selectedProject, 'assignees'], (assignees) =>
            assignees.add(addedUser.email)
          );

    return updatedState;
  },

  setSelectedUsers: ({ state }: AppStateActionParams, user: User): AppState => {
    const { selectedUsers } = state();

    let updatedUsers: List<User> = selectedUsers;
    if (selectedUsers.indexOf(user) === -1) {
      updatedUsers = selectedUsers.push(user);
    } else {
      updatedUsers = selectedUsers.delete(selectedUsers.indexOf(user));
    }

    return state().set('selectedUsers', updatedUsers);
  },

  clearAllSelectedUsers: ({ state }: AppStateActionParams): AppState => {
    return state().set('selectedUsers', List<User>());
  },

  removeUsersFromProject: async (
    { state, actions }: AppStateActionParams,
    usersToRemove: List<User>
  ): Promise<AppState> => {
    const { selectedProject } = state();

    await Promise.all(
      [...usersToRemove].map(
        (user: User): Promise<Response> =>
          removeUserFromProject(selectedProject, user.id)
      )
    );

    await actions.getUsers();
    await actions.getRoles();
    await actions.queryUsers();
    // Refetching the project because the project assignees have changed.
    actions.getProject(selectedProject);

    return state();
  },

  canPurchase: async ({ state }: AppStateActionParams): Promise<AppState> => {
    const userCanPurchase = await canPurchase();

    return state().set('canPurchase', userCanPurchase);
  },
};

export default userActions;
